import type { RoleFunctions } from './role.interface';

// Holders may search every driver's task history within scope; others are always restricted to their own.
export const TASKS_LIST_ALL_PERMISSION = 'tasks:list-all';

// Grants access to POST /tasks/history/export (CSV export of the caller's filtered task history).
export const TASKS_EXPORT_HISTORY_PERMISSION = 'tasks:export-history';

export const BUILT_IN_ROLE_KEY = {
  ACCOUNT_MANAGER: 'ACCOUNT_MANAGER',
  CARRIER: 'CARRIER',
  DRIVER: 'DRIVER',
  SITE_MANAGER: 'SITE_MANAGER',
  SITE_OPERATOR: 'SITE_OPERATOR',
  SPACE_ADMIN: 'SPACE_ADMIN',
  TENANT_ADMIN: 'TENANT_ADMIN',
} as const;

export type BuiltInRoleKey = (typeof BUILT_IN_ROLE_KEY)[keyof typeof BUILT_IN_ROLE_KEY];

export interface BuiltInRoleDefinition {
  functions?: RoleFunctions;
  permissions: string[];
}

const builtInRoles: Record<BuiltInRoleKey, BuiltInRoleDefinition> = {
  [BUILT_IN_ROLE_KEY.ACCOUNT_MANAGER]: {
    permissions: [
      'backoffice:access',
      'invitations:manage',
      'invitations:read',
      'permissions:read',
      'roles:manage',
      'roles:read',
      'spaces:list',
      'spaces:manage',
      'tenant-defaults:read',
      'tenants:initialize',
      'tenants:manage',
      'tenants:read',
      'tracking:read',
      'users:list',
      'users:manage',
    ],
  },
  [BUILT_IN_ROLE_KEY.CARRIER]: { permissions: ['frontoffice:access', 'tasks:list'] },
  [BUILT_IN_ROLE_KEY.DRIVER]: {
    functions: { isDriver: true },
    permissions: [
      'drivers:list',
      'mobile:access',
      'orders:read',
      'shippers:list',
      'sites:list',
      'tasks:execute',
      'tasks:export-history',
      'tasks:list',
      'tasks:release',
      'tasks:reorder',
      'tasks:self-assign',
    ],
  },
  [BUILT_IN_ROLE_KEY.SITE_MANAGER]: {
    permissions: [
      'carriers-sites:list',
      'carriers:access-read-only',
      'carriers:list',
      'carriers:manage',
      'customers:list',
      'customers:manage',
      'drivers:list',
      'drivers:manage',
      'endpoints:manage',
      'endpoints:read',
      'frontoffice:access',
      'handling-unit-types:list',
      'kpis:read',
      'order-types:list',
      'orders:cancel',
      'orders:list',
      'orders:manage',
      'orders:read',
      'orders:write',
      'permissions:read',
      'roles:read',
      'shippers:list',
      'sites:access-read-only',
      'sites:list',
      'sites:manage',
      'sites:update',
      'tasks:assign',
      'tasks:list',
      'tasks:list-all',
      'tenant-defaults:read',
      'tracking:read',
      'users:access-read-only',
      'users:list',
      'users:manage',
      'vehicle-templates:list',
      'vehicles:list',
    ],
  },
  [BUILT_IN_ROLE_KEY.SITE_OPERATOR]: {
    permissions: [
      'carriers-sites:list',
      'carriers:list',
      'customers:list',
      'drivers:list',
      'frontoffice:access',
      'handling-unit-types:list',
      'kpis:read',
      'order-types:list',
      'orders:cancel',
      'orders:list',
      'orders:manage',
      'orders:read',
      'orders:write',
      'permissions:read',
      'roles:read',
      'shippers:list',
      'sites:access-read-only',
      'sites:list',
      'tasks:assign',
      'tasks:list',
      'tasks:list-all',
      'tenant-defaults:read',
      'tracking:read',
      'users:list',
      'vehicle-templates:list',
      'vehicles:list',
    ],
  },
  [BUILT_IN_ROLE_KEY.SPACE_ADMIN]: {
    permissions: [
      'carriers-sites:list',
      'carriers-sites:manage',
      'carriers:access-read-only',
      'carriers:list',
      'carriers:manage',
      'customers:list',
      'customers:manage',
      'drivers:list',
      'endpoints:manage',
      'endpoints:read',
      'frontoffice:access',
      'handling-unit-types:list',
      'kpis:read',
      'order-types:list',
      'order-types:manage',
      'orders:cancel',
      'orders:list',
      'orders:manage',
      'orders:read',
      'orders:write',
      'permissions:read',
      'roles:manage',
      'roles:read',
      'shippers:list',
      'sites:access-read-only',
      'sites:create',
      'sites:list',
      'sites:manage',
      'sites:update',
      'spaces:access-read-only',
      'spaces:list',
      'spaces:manage',
      'tasks:assign',
      'tasks:list',
      'tasks:list-all',
      'tenant-defaults:read',
      'users:access-read-only',
      'users:list',
      'users:manage',
      'vehicle-templates:list',
      'vehicle-templates:manage',
      'vehicles:list',
      'vehicles:manage',
    ],
  },
  [BUILT_IN_ROLE_KEY.TENANT_ADMIN]: {
    permissions: [
      'carriers-sites:list',
      'carriers-sites:manage',
      'carriers:access-read-only',
      'carriers:list',
      'carriers:manage',
      'customers:list',
      'customers:manage',
      'drivers:list',
      'drivers:manage',
      'endpoints:manage',
      'endpoints:read',
      'frontoffice:access',
      'handling-unit-types:list',
      'handling-unit-types:manage',
      'invitations:manage',
      'invitations:read',
      'kpis:read',
      'order-types:list',
      'order-types:manage',
      'orders:cancel',
      'orders:list',
      'orders:manage',
      'orders:read',
      'orders:write',
      'permissions:read',
      'roles:manage',
      'roles:read',
      'servicekeys:manage',
      'servicekeys:read',
      'shippers:list',
      'shippers:manage',
      'sites:access-read-only',
      'sites:create',
      'sites:list',
      'sites:manage',
      'sites:update',
      'spaces:access-read-only',
      'spaces:list',
      'spaces:manage',
      'tasks:assign',
      'tasks:list',
      'tasks:list-all',
      'tenant-defaults:read',
      'tenant-defaults:write',
      'tenants:access-read-only',
      'tenants:initialize',
      'tenants:manage',
      'tenants:read',
      'tracking:read',
      'users:access-read-only',
      'users:list',
      'users:manage',
      'vehicle-templates:list',
      'vehicle-templates:manage',
      'vehicles:list',
      'vehicles:manage',
      'webhooks:manage',
      'webhooks:read',
    ],
  },
};

export default builtInRoles;
